// ==========================================================================
// AUTHENTICATION & ACCESS CONTROL MODULE
// WuRuPayroll - Login, Password Setup & Reset, Google Sign-In, Session
// ==========================================================================

import {
  collection, query, where, getDocs
} from "firebase/firestore";

import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signOut
} from "firebase/auth";

import { db, auth, clearTimesheetCache } from "../firebase-db.js";

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing and shell
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const rebuildAppShellHTML = () => {
  if (typeof window !== 'undefined' && window.rebuildAppShellHTML) {
    return window.rebuildAppShellHTML();
  }
};

const setupThemeBranding = () => {
  if (typeof window !== 'undefined' && window.setupThemeBranding) {
    return window.setupThemeBranding();
  }
};

function renderLoginPage() {
  document.body.innerHTML = `
    <div class="login-body">
      <div class="login-card">
        <img class="login-logo" src="icon-512.png" alt="WuRu Logo">
        <div class="login-title-group">
          <h2>WuRu Payroll PWA</h2>
          <p>กรุณาลงชื่อเข้าใช้งานเพื่อเข้าสู่ระบบงานเงินเดือน</p>
        </div>
        <div id="login-error" style="color: var(--danger); font-size: 0.9rem; font-weight: 600; display: none; margin-bottom: 10px;">
          อีเมลหรือรหัสผ่านไม่ถูกต้อง!
        </div>
        <form id="login-form">
          <div class="form-group" style="text-align: left; margin-bottom: 15px;">
            <label>อีเมล (Email)</label>
            <input type="email" id="login-email" required placeholder="example@gmail.com...">
          </div>
          <div class="form-group" style="text-align: left; margin-bottom: 15px;">
            <label>รหัสผ่าน (Password)</label>
            <input type="password" id="login-pass" required placeholder="กรอกรหัสผ่านของคุณ...">
          </div>
          <div style="text-align: right; margin-top: -10px; margin-bottom: 15px;">
            <a href="#" id="link-forgot-password" style="font-size: 0.85rem; color: var(--primary); text-decoration: none; font-weight: 500;">ตั้งค่ารหัสผ่านครั้งแรก / ลืมรหัสผ่าน</a>
          </div>
          <button type="submit" class="btn btn-primary" style="width: 100%; margin-top: 5px;">เข้าสู่ระบบด้วยอีเมล</button>
        </form>
        <div class="login-divider">หรือ</div>
        <button type="button" id="btn-google-login" class="btn-google">
          <svg viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
          </svg>
          ลงชื่อเข้าใช้งานด้วย Google
        </button>
      </div>
    </div>
  `;

  // Helper function to handle successfully authenticated user
  const handleAuthSuccess = (authenticatedUser) => {
    state.currentUser = authenticatedUser;
    localStorage.setItem('wuru_user', JSON.stringify(authenticatedUser));

    const isMasterAdmin = authenticatedUser.id === 'emp_master' || authenticatedUser.role === 'admin' || authenticatedUser.role === 'superadmin';
    if (isMasterAdmin) {
      localStorage.setItem('wuru_active_company', 'itembox');
      state.activeCompanyId = 'itembox';
    } else {
      localStorage.setItem('wuru_active_company', authenticatedUser.companyId);
      state.activeCompanyId = authenticatedUser.companyId;
    }
    sessionStorage.setItem('wuru_session_started', 'true');

    // Rebuild full app shell HTML
    rebuildAppShellHTML();
    setupThemeBranding();
    navigateTo('dashboard');
  };

  // Bind Email/Password Submit
  document.getElementById('login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const emailInput = document.getElementById('login-email').value.trim().toLowerCase();
    const passInput = document.getElementById('login-pass').value;

    const loginError = document.getElementById('login-error');
    loginError.style.display = 'none';

    try {
      // 1. Authenticate with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, emailInput, passInput);
      const authEmail = userCredential.user.email.toLowerCase().trim();

      // 2. Query employees collection where email matches
      const q = query(collection(db, 'employees'), where('email', '==', authEmail));
      const snap = await getDocs(q);

      let authenticatedUser = null;
      let isSuspended = false;
      let hasEmployeeRecord = false;

      snap.forEach((doc) => {
        hasEmployeeRecord = true;
        const u = doc.data();
        if (u.status !== 'active') {
          isSuspended = true;
        } else {
          let isLoginAllowed = true;
          if (u.loginAccount) {
            if (u.loginAccount.enabled === false) {
              isLoginAllowed = false;
            }
            if (u.loginAccount.deactivationDate) {
              const todayStr = new Date().toISOString().split('T')[0];
              if (todayStr >= u.loginAccount.deactivationDate) {
                isLoginAllowed = false;
              }
            }
          }
          if (isLoginAllowed) {
            authenticatedUser = { id: doc.id, ...u };
          } else {
            isSuspended = true;
          }
        }
      });

      if (authenticatedUser) {
        handleAuthSuccess(authenticatedUser);
      } else if (isSuspended) {
        loginError.textContent = "บัญชีเข้าใช้งานของคุณถูกระงับหรือปิดใช้งานแล้ว";
        loginError.style.display = 'block';
      } else if (!hasEmployeeRecord) {
        loginError.textContent = "ไม่พบบัญชีพนักงานที่เชื่อมโยงกับอีเมลนี้ในระบบบริษัท";
        loginError.style.display = 'block';
      } else {
        loginError.textContent = "บัญชีเข้าใช้งานของคุณถูกระงับหรือปิดใช้งานแล้ว";
        loginError.style.display = 'block';
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' ||
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/wrong-password') {
        loginError.textContent = "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
      } else {
        loginError.textContent = "ระบบขัดข้อง โปรดลองใหม่อีกครั้ง";
      }
      loginError.style.display = 'block';
    }
  });

  // Bind Google Login Click
  document.getElementById('btn-google-login').addEventListener('click', async () => {
    const loginError = document.getElementById('login-error');
    loginError.style.display = 'none';

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const googleEmail = result.user.email.toLowerCase().trim();

      // Query employees collection where email matches Google account email
      const q = query(collection(db, 'employees'), where('email', '==', googleEmail));
      const snap = await getDocs(q);

      let authenticatedUser = null;
      let isSuspended = false;

      snap.forEach((doc) => {
        const u = doc.data();
        if (u.status !== 'active') {
          isSuspended = true;
        } else {
          let isLoginAllowed = true;
          if (u.loginAccount) {
            if (u.loginAccount.enabled === false) {
              isLoginAllowed = false;
            }
            if (u.loginAccount.deactivationDate) {
              const todayStr = new Date().toISOString().split('T')[0];
              if (todayStr >= u.loginAccount.deactivationDate) {
                isLoginAllowed = false;
              }
            }
          }
          if (isLoginAllowed) {
            authenticatedUser = { id: doc.id, ...u };
          } else {
            isSuspended = true;
          }
        }
      });

      if (authenticatedUser) {
        handleAuthSuccess(authenticatedUser);
      } else if (isSuspended) {
        loginError.textContent = "บัญชีเข้าใช้งานของคุณถูกระงับหรือปิดใช้งานแล้ว";
        loginError.style.display = 'block';
      } else {
        loginError.textContent = `ไม่พบบัญชีพนักงานที่เชื่อมโยงกับอีเมล Google (${googleEmail}) ในระบบ`;
        loginError.style.display = 'block';
      }
    } catch (err) {
      console.error("Google Sign-In Error:", err);
      // Handle closed popup or other Firebase auth cancel events gracefully
      if (err.code !== 'auth/popup-closed-by-user') {
        loginError.textContent = "การลงชื่อเข้าใช้งานด้วย Google ล้มเหลว โปรดลองใหม่อีกครั้ง";
        loginError.style.display = 'block';
      }
    }
  });

  // Bind Forgot Password Link Click
  document.getElementById('link-forgot-password').addEventListener('click', (e) => {
    e.preventDefault();
    showForgotPasswordModal();
  });
}

// ==========================================================================
// PASSWORD SETUP & RESET FUNCTIONALITY
// ==========================================================================
function showForgotPasswordModal () {
  let modalOverlay = document.getElementById('forgot-password-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'forgot-password-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 450px;">
      <div class="modal-header">
        <h3>🔑 ตั้งค่า / รีเซ็ตรหัสผ่าน</h3>
        <button class="btn-close-modal" onclick="document.getElementById('forgot-password-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body" style="padding-top: 10px;">
        <p style="color: var(--text-sub); font-size: 0.9rem; margin-bottom: 15px; line-height: 1.5;">
          ระบุอีเมลบริษัทของคุณที่ใช้ลงทะเบียนในระบบป้อนข้อมูลลงเวลา เพื่อตั้งค่ารหัสผ่านใหม่หรือตั้งค่าสำหรับเข้าใช้งานครั้งแรก ระบบจะส่งอีเมลพร้อมลิงก์ตั้งค่าไปยังอีเมลนั้นโดยตรง
        </p>
        <div class="form-group" style="text-align: left; margin-bottom: 15px;">
          <label style="font-weight: 600; font-size: 0.9rem; margin-bottom: 5px; display: block;">อีเมล (Email) *</label>
          <input type="email" id="reset-email" required placeholder="example@gmail.com" style="width: 100%; padding: 10px; border: 1px solid var(--gray-300); border-radius: var(--border-radius-sm);">
        </div>
        <div id="reset-status" style="margin-top: 10px; font-size: 0.85rem; font-weight: 600; line-height: 1.4; display: none;"></div>
      </div>
      <div class="modal-footer" style="display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid var(--gray-200); padding-top: 15px; margin-top: 15px;">
        <button class="btn btn-secondary" onclick="document.getElementById('forgot-password-modal-overlay').classList.remove('active')">ยกเลิก</button>
        <button class="btn btn-primary" id="btn-submit-reset" onclick="submitPasswordReset()">ส่งอีเมลตั้งรหัสผ่าน</button>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');
};

async function submitPasswordReset () {
  const emailInput = document.getElementById('reset-email').value.trim().toLowerCase();
  const resetStatus = document.getElementById('reset-status');
  const btnSubmit = document.getElementById('btn-submit-reset');

  if (!emailInput) {
    resetStatus.textContent = "กรุณากรอกอีเมลสำหรับตั้งค่ารหัสผ่าน";
    resetStatus.style.color = "var(--danger)";
    resetStatus.style.display = "block";
    return;
  }

  // Disable button to prevent double clicks
  btnSubmit.disabled = true;
  resetStatus.textContent = "กำลังตรวจสอบสิทธิ์พนักงาน...";
  resetStatus.style.color = "var(--primary)";
  resetStatus.style.display = "block";

  try {
    // 1. Query Firestore for the employee profile with this email
    const q = query(collection(db, 'employees'), where('email', '==', emailInput));
    const snap = await getDocs(q);

    let employeeFound = null;
    snap.forEach((doc) => {
      employeeFound = { id: doc.id, ...doc.data() };
    });

    if (!employeeFound) {
      resetStatus.textContent = "ไม่พบบัญชีพนักงานที่เชื่อมโยงกับอีเมลนี้ในระบบบริษัท";
      resetStatus.style.color = "var(--danger)";
      btnSubmit.disabled = false;
      return;
    }

    // Check if employee is inactive or resigned
    if (employeeFound.status !== 'active') {
      let isAllowed = false;
      if (employeeFound.loginAccount && employeeFound.loginAccount.deactivationDate) {
        const todayStr = new Date().toISOString().split('T')[0];
        if (todayStr < employeeFound.loginAccount.deactivationDate) {
          isAllowed = true;
        }
      }
      if (!isAllowed) {
        resetStatus.textContent = "บัญชีพนักงานของคุณถูกระงับสิทธิ์หรือลาออกแล้ว ไม่สามารถตั้งรหัสผ่านได้";
        resetStatus.style.color = "var(--danger)";
        btnSubmit.disabled = false;
        return;
      }
    }

    // 2. Try to send password reset email via Firebase Auth
    try {
      await sendPasswordResetEmail(auth, emailInput);
      resetStatus.textContent = "ระบบได้ส่งลิงก์สำหรับตั้งค่า/รีเซ็ตรหัสผ่านไปยังอีเมลของคุณเรียบร้อยแล้ว โปรดตรวจสอบกล่องข้อความเข้า (Inbox) รวมถึงในกล่องจดหมายขยะ (Junk/Spam)";
      resetStatus.style.color = "var(--success)";
    } catch (authErr) {
      if (authErr.code === 'auth/user-not-found') {
        // User does not exist in Firebase Auth yet, so we create the user account
        // and then send the password reset email.
        const tempPassword = Math.random().toString(36) + 'Aa1!_' + Math.random().toString(36);
        await createUserWithEmailAndPassword(auth, emailInput, tempPassword);
        // Immediately sign out to prevent auto-login session hijacking
        await signOut(auth);
        // Now send reset email
        await sendPasswordResetEmail(auth, emailInput);
        resetStatus.textContent = "ระบบได้ส่งลิงก์สำหรับตั้งค่า/รีเซ็ตรหัสผ่านครั้งแรกไปยังอีเมลของคุณเรียบร้อยแล้ว โปรดตรวจสอบกล่องข้อความเข้า (Inbox) รวมถึงในกล่องจดหมายขยะ (Junk/Spam)";
        resetStatus.style.color = "var(--success)";
      } else {
        throw authErr;
      }
    }
  } catch (err) {
    console.error("Password reset error:", err);
    resetStatus.textContent = "เกิดข้อผิดพลาด: " + (err.message || "ระบบขัดข้อง โปรดลองใหม่อีกครั้ง");
    resetStatus.style.color = "var(--danger)";
    btnSubmit.disabled = false;
  }
};



function logout() {
  clearTimesheetCache();
  localStorage.removeItem('wuru_user');
  localStorage.removeItem('wuru_active_company');
  sessionStorage.removeItem('wuru_session_started');
  state.currentUser = null;
  renderLoginPage();
}

// Bind auth functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.renderLoginPage = renderLoginPage;
  window.showForgotPasswordModal = showForgotPasswordModal;
  window.submitPasswordReset = submitPasswordReset;
  window.logout = logout;
}

export {
  renderLoginPage,
  showForgotPasswordModal,
  submitPasswordReset,
  logout
};
